import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { CircleHelp, Loader } from "lucide-react";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { useUpdateSKUMutation } from "@/data/mutations";
import { ProductTabOptions } from "@/data/types";
import { updateProductTabOptions } from "@/lib/cms";

import { useCMSContext } from "../cms-context";
import { useCMSTabContext } from "../cms-tab-context";

const cmsProductTabFormSchema = z.object({
  show_serial_number: z.boolean(),
});

type Props = {
  options: ProductTabOptions;
};

export const ProductTabSettings: FC<Props> = ({ options }) => {
  const form = useForm<z.infer<typeof cmsProductTabFormSchema>>({
    resolver: zodResolver(cmsProductTabFormSchema),
    defaultValues: {
      show_serial_number: get(options, "show_serial_number", true),
    },
  });

  const { sku, pageContent } = useCMSContext();
  const { tabIndex } = useCMSTabContext();
  const updateSKUMutation = useUpdateSKUMutation(sku.id);

  const onSubmit = (data: z.infer<typeof cmsProductTabFormSchema>) => {
    updateSKUMutation.mutate({
      page_content: updateProductTabOptions(pageContent, data, tabIndex),
    });
  };

  return (
    <Card>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardHeader className="flex flex-col gap-6">
            <div className="flex flex-1 justify-center">
              <img
                src="/images/product-illustration.png"
                alt="Product Illustration"
                className="w-[280px] h-[190px]"
              />
            </div>
            <FormField
              control={form.control}
              name="show_serial_number"
              render={({ field }) => (
                <FormItem className="flex space-x-2 mt-6">
                  <FormLabel className="font-normal gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="grid gap-1.5 leading-none">
                      <span className="font-medium my-0.5">
                        Show Serial Number
                      </span>
                      <FormDescription>
                        Display each product's unique serial number on the
                        digital experience to represent rarity and exclusivity
                      </FormDescription>
                    </div>
                  </FormLabel>
                </FormItem>
              )}
            />
            <div className="flex gap-2 text-muted-foreground">
              <CircleHelp className="size-5" />
              <p className="text-sm">
                The Product name, image, and description is used display the
                product in your Webpage. To change this information, edit the
                Product from the SKU page.
              </p>
            </div>
          </CardHeader>
          <div className="border-t px-6 py-4">
            <Button type="submit" disabled={updateSKUMutation.isPending}>
              {updateSKUMutation.isPending && (
                <Loader className="animate-spin" />
              )}
              Save
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
};
